import React, { useState } from "react";
import { Icon } from "../../UIElements/Icon";

const WidgetConfigurator = ({ config, widgetType, onChange }) => {
  const [copiedUrl, setCopiedUrl] = useState(false);

  const handleCopyUrl = async () => {
    if (!config.chatUrl) return;
    try {
      await navigator.clipboard.writeText(config.chatUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };
  const handleNumericChange = (key, value) => {
    // Only allow digits, max 4 digits (9999)
    if (value !== "" && !/^\d*$/.test(value)) return;
    if (value.length > 4) return;
    onChange(key, value);
  };

  const handleNumericBlur = (key, defaultValue, min) => {
    const numValue = parseInt(config[key], 10);
    if (isNaN(numValue) || config[key] === "") {
      onChange(key, defaultValue);
    } else if (numValue < min) {
      onChange(key, min);
    }
  };

  const positions = [
    { value: "bottom-left", label: "Bottom Left", iconName: "ArrowDownLeft" },
    {
      value: "bottom-right",
      label: "Bottom Right",
      iconName: "ArrowDownRight",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Chat URL */}
      <div className="space-y-3">
        <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
          <Icon name="Link" className="text-brand-cyan h-4 w-4" />
          Chat URL
        </label>
        <div className="flex gap-2">
          <input
            type="url"
            value={config.chatUrl}
            disabled={true}
            onChange={(e) => onChange("chatUrl", e.target.value)}
            placeholder="https://your-chat.finitless.ai/"
            className="bg-muted border-border text-card-foreground placeholder-muted-foreground focus:ring-brand-cyan flex-1 rounded-lg border px-4 py-3 transition-all focus:border-transparent focus:ring-2 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleCopyUrl}
            disabled={!config.chatUrl}
            className="bg-background border-border text-card-foreground hover:bg-accent rounded-lg border px-4 py-3 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            title="Copy URL"
          >
            <Icon
              name={copiedUrl ? "Check" : "Copy"}
              className={`h-5 w-5 ${copiedUrl ? "text-green-500" : ""}`}
            />
          </button>
        </div>
        <p className="text-muted-foreground text-xs">
          The URL of your Finitless AI chat application
        </p>
      </div>

      {/* Icon URL (only for floating) */}
      {widgetType === "floating" && (
        <div className="space-y-3">
          <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
            <Icon name="Image" className="text-brand-purple h-4 w-4" />
            Button Icon URL (optional)
          </label>
          <div className="flex gap-3">
            {config.iconUrl && (
              <div className="border-border bg-background flex h-12 w-14 items-center justify-center overflow-hidden rounded-lg border">
                <img
                  src={config.iconUrl}
                  alt="Icon preview"
                  className="h-8 w-8 object-contain"
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            )}
            <input
              type="url"
              value={config.iconUrl || ""}
              onChange={(e) => onChange("iconUrl", e.target.value)}
              placeholder="https://example.com/icon.png"
              className="bg-background border-border text-card-foreground placeholder-muted-foreground focus:ring-brand-cyan flex-1 rounded-lg border px-4 py-3 transition-all focus:border-transparent focus:ring-2 focus:outline-none"
            />
          </div>
          <p className="text-muted-foreground text-xs">
            Custom icon for the chat button. Leave empty to use the default chat
            icon.
          </p>
        </div>
      )}

      {/* Colors */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
            <Icon name="Palette" className="text-brand-magenta h-4 w-4" />
            Primary Color
          </label>
          <div className="flex gap-3">
            <div
              className="border-border relative h-12 w-14 cursor-pointer overflow-hidden rounded-lg border"
              style={{ backgroundColor: config.primaryColor }}
            >
              <input
                type="color"
                value={config.primaryColor}
                onChange={(e) => onChange("primaryColor", e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </div>
            <input
              type="text"
              value={config.primaryColor}
              onChange={(e) => onChange("primaryColor", e.target.value)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan flex-1 rounded-lg border px-4 py-3 font-mono text-sm focus:ring-2 focus:outline-none"
            />
          </div>
        </div>

        <div className="space-y-3">
          <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
            <Icon name="Palette" className="text-brand-purple h-4 w-4" />
            Secondary Color
          </label>
          <div className="flex gap-3">
            <div
              className="border-border relative h-12 w-14 cursor-pointer overflow-hidden rounded-lg border"
              style={{ backgroundColor: config.secondaryColor }}
            >
              <input
                type="color"
                value={config.secondaryColor}
                onChange={(e) => onChange("secondaryColor", e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </div>
            <input
              type="text"
              value={config.secondaryColor}
              onChange={(e) => onChange("secondaryColor", e.target.value)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan flex-1 rounded-lg border px-4 py-3 font-mono text-sm focus:ring-2 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Gradient Preview */}
      <div className="border-border rounded-xl border p-4">
        <p className="text-muted-foreground mb-3 text-sm">Gradient preview:</p>
        <div
          className="h-16 rounded-lg"
          style={{
            background: `linear-gradient(135deg, ${config.primaryColor} 0%, ${config.secondaryColor} 100%)`,
          }}
        />
      </div>

      {/* Position (for floating only) */}
      {widgetType === "floating" && (
        <div className="space-y-3">
          <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
            <Icon name="Move" className="text-brand-cyan h-4 w-4" />
            Button Position
          </label>
          <div className="grid grid-cols-2 gap-3">
            {positions.map((pos) => (
              <button
                key={pos.value}
                onClick={() => onChange("position", pos.value)}
                className={`flex items-center justify-center gap-2 rounded-lg border-2 p-4 transition-all ${
                  config.position === pos.value
                    ? "border-brand-cyan bg-brand-cyan/10"
                    : "border-border hover:border-brand-cyan/50"
                } `}
              >
                <Icon name={pos.iconName} className="text-brand-cyan h-6 w-6" />
                <span className="text-card-foreground text-sm">
                  {pos.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sizes (for floating only) */}
      {widgetType === "floating" && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="space-y-3">
            <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
              <Icon name="Circle" className="text-brand-magenta h-4 w-4" />
              Button Size (min. 40px)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={config.buttonSize}
              onChange={(e) =>
                handleNumericChange("buttonSize", e.target.value)
              }
              onBlur={() => handleNumericBlur("buttonSize", 60, 40)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
            />
          </div>

          <div className="space-y-3">
            <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
              <Icon
                name="ArrowLeftRight"
                className="text-brand-purple h-4 w-4"
              />
              Chat Width (min. 300px)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={config.chatWidth}
              onChange={(e) => handleNumericChange("chatWidth", e.target.value)}
              onBlur={() => handleNumericBlur("chatWidth", 370, 300)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
            />
          </div>

          <div className="space-y-3">
            <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
              <Icon name="ArrowUpDown" className="text-brand-cyan h-4 w-4" />
              Chat Height (min. 400px)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={config.chatHeight}
              onChange={(e) =>
                handleNumericChange("chatHeight", e.target.value)
              }
              onBlur={() => handleNumericBlur("chatHeight", 570, 400)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
            />
          </div>
        </div>
      )}

      {/* Inline sizes */}
      {widgetType === "inline" && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
              <Icon
                name="ArrowLeftRight"
                className="text-brand-purple h-4 w-4"
              />
              Max Width (min. 300px)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={config.chatWidth}
              onChange={(e) => handleNumericChange("chatWidth", e.target.value)}
              onBlur={() => handleNumericBlur("chatWidth", 450, 300)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
            />
          </div>

          <div className="space-y-3">
            <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
              <Icon name="ArrowUpDown" className="text-brand-cyan h-4 w-4" />
              Height (min. 400px)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={config.chatHeight}
              onChange={(e) =>
                handleNumericChange("chatHeight", e.target.value)
              }
              onBlur={() => handleNumericBlur("chatHeight", 600, 400)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
            />
          </div>
        </div>
      )}

      {/* Popup sizes */}
      {widgetType === "popup" && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
              <Icon
                name="ArrowLeftRight"
                className="text-brand-purple h-4 w-4"
              />
              Max Width (min. 300px)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={config.chatWidth}
              onChange={(e) => handleNumericChange("chatWidth", e.target.value)}
              onBlur={() => handleNumericBlur("chatWidth", 450, 300)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
            />
          </div>

          <div className="space-y-3">
            <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
              <Icon name="ArrowUpDown" className="text-brand-cyan h-4 w-4" />
              Max Height (min. 400px)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={config.chatHeight}
              onChange={(e) =>
                handleNumericChange("chatHeight", e.target.value)
              }
              onBlur={() => handleNumericBlur("chatHeight", 650, 400)}
              className="bg-background border-border text-card-foreground focus:ring-brand-cyan w-full rounded-lg border px-4 py-3 focus:ring-2 focus:outline-none"
            />
          </div>
        </div>
      )}

      {/* Show Header toggle (for popup only) */}
      {widgetType === "popup" && (
        <div className="space-y-3">
          <label className="text-card-foreground flex items-center gap-2 text-sm font-medium">
            <Icon name="PanelTop" className="text-brand-magenta h-4 w-4" />
            Modal Header
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => onChange("showHeader", true)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg border-2 p-4 transition-all ${
                config.showHeader
                  ? "border-brand-cyan bg-brand-cyan/10"
                  : "border-border hover:border-brand-cyan/50"
              } `}
            >
              <Icon name="Check" className="text-brand-cyan h-5 w-5" />
              <span className="text-card-foreground text-sm">With Header</span>
            </button>
            <button
              onClick={() => onChange("showHeader", false)}
              className={`flex flex-1 items-center justify-center gap-2 rounded-lg border-2 p-4 transition-all ${
                !config.showHeader
                  ? "border-brand-cyan bg-brand-cyan/10"
                  : "border-border hover:border-brand-cyan/50"
              } `}
            >
              <Icon name="X" className="text-brand-cyan h-5 w-5" />
              <span className="text-card-foreground text-sm">
                Without Header
              </span>
            </button>
          </div>
          <p className="text-muted-foreground text-xs">
            {config.showHeader
              ? "The modal will have a gradient header with a close button"
              : "The modal will display the chat directly without a header bar"}
          </p>
        </div>
      )}
    </div>
  );
};

export default WidgetConfigurator;
